'use client';

import { useCommandState } from 'cmdk';
import type { LucideProps } from 'lucide-react';
import {
  AwardIcon,
  BookmarkIcon,
  BoxIcon,
  BriefcaseBusinessIcon,
  CornerDownLeftIcon,
  FileTextIcon,
  GaugeIcon,
  GraduationCapIcon,
  LayersIcon,
  MicIcon,
  MoonStarIcon,
  QuoteIcon,
  SunMediumIcon,
  TextInitialIcon,
  TrendingUpIcon,
  TypeIcon,
} from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { SOCIAL_LINKS } from '@/features/portfolio/data/social-links';
import { playSfx } from '@/hooks/use-sound';
import { trackEvent } from '@/lib/events';
import { copyText } from '@/utils/copy';
import { Icons } from './icons';
import { Button } from './ui/button';
import { Kbd, KbdGroup } from './ui/kbd';
import { Separator } from './ui/separator';
import { getMarkSVG, VictorMark } from './victor-mark';
import { getWordmarkSVG } from './victor-wordmark';

type CommandLinkItem = {
  title: string;
  href: string;

  icon?: React.ComponentType<LucideProps>;
  iconImage?: string;
  keywords?: string[];
  openInNewTab?: boolean;
};

export type CommandMenuPost = {
  slug: string;
  title: string;
  description?: string;
};

export type CommandMenuStudy = {
  slug: string;
  title: string;
  description?: string;
};

const MENU_LINKS: CommandLinkItem[] = [
  {
    title: 'Portfolio',
    href: '/',
    icon: VictorMark,
  },
  {
    title: 'Case studies',
    href: '/case-studies',
    icon: TrendingUpIcon,
    keywords: ['case', 'studies', 'wins', 'latency', 'reliability'],
  },
  {
    title: 'Blog',
    href: '/blog',
    icon: FileTextIcon,
    keywords: ['blog', 'posts', 'writing'],
  },
  {
    title: 'PVC Cockpit',
    href: '/pvc-cockpit',
    icon: GaugeIcon,
    keywords: ['pvc', 'cockpit', 'dashboard'],
  },
];

const PORTFOLIO_LINKS: CommandLinkItem[] = [
  {
    title: 'About',
    href: '/#about',
    icon: TextInitialIcon,
  },
  {
    title: 'Testimonials',
    href: '/#testimonials',
    icon: QuoteIcon,
  },
  {
    title: 'Tech Stack',
    href: '/#stack',
    icon: LayersIcon,
  },
  {
    title: 'Experience',
    href: '/#experience',
    icon: BriefcaseBusinessIcon,
  },
  {
    title: 'Education',
    href: '/#education',
    icon: GraduationCapIcon,
  },
  {
    title: 'Projects',
    href: '/#projects',
    icon: BoxIcon,
  },
  {
    title: 'Honors & Awards',
    href: '/#awards',
    icon: AwardIcon,
  },
  {
    title: 'Podcasts & Talks',
    href: '/#media',
    icon: MicIcon,
  },
  {
    title: 'Bookmarks',
    href: '/#bookmarks',
    icon: BookmarkIcon,
  },
];

const SOCIAL_LINK_ITEMS: CommandLinkItem[] = SOCIAL_LINKS.map((item) => ({
  title: item.title,
  href: item.href,
  iconImage: item.icon,
  openInNewTab: true,
}));

const OTHER_LINK_ITEMS: CommandLinkItem[] = [
  {
    title: 'llms.txt',
    href: '/llms.txt',
    icon: FileTextIcon,
    openInNewTab: true,
  },
];

export function CommandMenu({
  posts,
  studies = [],
}: {
  posts: CommandMenuPost[];
  studies?: CommandMenuStudy[];
}) {
  const router = useRouter();

  const { setTheme, resolvedTheme } = useTheme();

  const [open, setOpen] = useState(false);

  // Single wiring point for open/close sounds: every path that changes the
  // palette's open state (hotkey, trigger button, Escape/overlay, item select)
  // funnels through here. Playing synchronously inside the originating
  // gesture's call stack (not in an effect) keeps Safari's autoplay policy
  // happy; the ref mirrors the current state so the hotkey can toggle without
  // playing inside a functional updater (StrictMode invokes those twice).
  const openRef = useRef(open);
  const changeOpen = useCallback((next: boolean) => {
    if (openRef.current !== next) {
      openRef.current = next;
      playSfx(next ? 'open' : 'close');
    }
    setOpen(next);
  }, []);

  useHotkeys('mod+k, slash', (e) => {
    e.preventDefault();

    const next = !openRef.current;
    if (next) {
      trackEvent({
        name: 'open_command_menu',
        properties: {
          method: 'keyboard',
          key: e.key === '/' ? '/' : e.metaKey ? 'cmd+k' : 'ctrl+k',
        },
      });
    }
    changeOpen(next);
  });

  const handleOpenLink = useCallback(
    (href: string, openInNewTab = false) => {
      playSfx('click');
      changeOpen(false);

      trackEvent({
        name: 'command_menu_action',
        properties: {
          action: 'navigate',
          href: href,
          open_in_new_tab: openInNewTab,
        },
      });

      if (openInNewTab) {
        window.open(href, '_blank', 'noopener');
      } else {
        router.push(href);
      }
    },
    [changeOpen, router]
  );

  const handleCopyText = useCallback(
    (text: string, message: string) => {
      playSfx('click');
      changeOpen(false);

      trackEvent({
        name: 'command_menu_action',
        properties: {
          action: 'copy',
          text: text,
        },
      });

      copyText(text);
      toast.success(message);
    },
    [changeOpen]
  );

  const createThemeHandler = useCallback(
    (theme: 'light' | 'dark' | 'system') => () => {
      playSfx('click');
      changeOpen(false);

      trackEvent({
        name: 'command_menu_action',
        properties: {
          action: 'change_theme',
          theme: theme,
        },
      });

      setTheme(theme);

      // if (!document.startViewTransition) {
      //   setTheme(theme);
      //   return;
      // }

      // document.startViewTransition(() => setTheme(theme));
    },
    [changeOpen, setTheme]
  );

  const blogLinks = useMemo(
    () =>
      posts.map(
        (post): CommandLinkItem => ({
          title: post.title,
          href: `/blog/${post.slug}`,
          keywords: post.description ? ['blog', post.description] : ['blog'],
        })
      ),
    [posts]
  );

  const studyLinks = useMemo(
    () =>
      studies.map(
        (study): CommandLinkItem => ({
          title: study.title,
          href: `/case-studies/${study.slug}`,
          keywords: study.description
            ? ['case study', study.description]
            : ['case study'],
        })
      ),
    [studies]
  );

  return (
    <>
      <Button
        variant="secondary"
        data-sfx-managed=""
        className="h-8 gap-1.5 rounded-full border border-input bg-white px-2.5 text-muted-foreground shadow-xs select-none hover:bg-white dark:bg-input/30 dark:hover:bg-input/30"
        aria-label="Search and navigation, Command K"
        onClick={() => {
          changeOpen(true);
          trackEvent({
            name: 'open_command_menu',
            properties: {
              method: 'click',
            },
          });
        }}
      >
        <Icons.search aria-hidden />

        <span className="font-sans text-sm/4 font-medium sm:hidden">
          Search
        </span>

        <KbdGroup aria-hidden className="hidden sm:in-[.os-macos_&]:flex">
          <Kbd className="w-5 min-w-5">⌘</Kbd>
          <Kbd className="w-5 min-w-5">K</Kbd>
        </KbdGroup>

        <KbdGroup aria-hidden className="hidden sm:not-[.os-macos_&]:flex">
          <Kbd>Ctrl</Kbd>
          <Kbd className="w-5 min-w-5">K</Kbd>
        </KbdGroup>
      </Button>

      <CommandDialog open={open} onOpenChange={changeOpen}>
        <CommandMenuInput />

        <CommandList className="min-h-80 supports-timeline-scroll:scroll-fade-effect-y">
          <CommandEmpty>No results found.</CommandEmpty>

          <CommandLinkGroup
            heading="Menu"
            links={MENU_LINKS}
            onLinkSelect={handleOpenLink}
          />

          <CommandLinkGroup
            heading="Portfolio"
            links={PORTFOLIO_LINKS}
            onLinkSelect={handleOpenLink}
          />

          <CommandLinkGroup
            heading="Case Studies"
            links={studyLinks}
            fallbackIcon={TrendingUpIcon}
            onLinkSelect={handleOpenLink}
          />

          <CommandLinkGroup
            heading="Blog"
            links={blogLinks}
            fallbackIcon={FileTextIcon}
            onLinkSelect={handleOpenLink}
          />

          <CommandLinkGroup
            heading="Social Links"
            links={SOCIAL_LINK_ITEMS}
            onLinkSelect={handleOpenLink}
          />

          <CommandGroup heading="Brand Assets">
            <CommandItem
              onSelect={() => {
                handleCopyText(
                  getMarkSVG(resolvedTheme === 'light' ? '#000' : '#fff'),
                  'Copied Mark as SVG'
                );
              }}
            >
              <VictorMark />
              Copy Mark as SVG
            </CommandItem>

            <CommandItem
              onSelect={() => {
                handleCopyText(
                  getWordmarkSVG(resolvedTheme === 'light' ? '#000' : '#fff'),
                  'Copied Logotype as SVG'
                );
              }}
            >
              <TypeIcon />
              Copy Logotype as SVG
            </CommandItem>
          </CommandGroup>

          <CommandGroup heading="Theme">
            <CommandItem
              keywords={['theme']}
              onSelect={createThemeHandler('light')}
            >
              <SunMediumIcon />
              Light
            </CommandItem>
            <CommandItem
              keywords={['theme']}
              onSelect={createThemeHandler('dark')}
            >
              <MoonStarIcon />
              Dark
            </CommandItem>
            <CommandItem
              keywords={['theme']}
              onSelect={createThemeHandler('system')}
            >
              <Icons.contrast />
              Auto
            </CommandItem>
          </CommandGroup>

          <CommandLinkGroup
            heading="Other"
            links={OTHER_LINK_ITEMS}
            onLinkSelect={handleOpenLink}
          />
        </CommandList>

        <CommandMenuFooter />
      </CommandDialog>
    </>
  );
}

function CommandMenuInput() {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (searchValue.length >= 2) {
      const timeoutId = setTimeout(() => {
        trackEvent({
          name: 'command_menu_search',
          properties: {
            query: searchValue,
            query_length: searchValue.length,
          },
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchValue]);

  return (
    <CommandInput
      placeholder="Type a command or search..."
      value={searchValue}
      onValueChange={setSearchValue}
    />
  );
}

function CommandLinkGroup({
  heading,
  links,
  fallbackIcon,
  onLinkSelect,
}: {
  heading: string;
  links: CommandLinkItem[];
  fallbackIcon?: React.ComponentType<LucideProps>;
  onLinkSelect: (href: string, openInNewTab?: boolean) => void;
}) {
  return (
    <CommandGroup heading={heading}>
      {links.map((link) => {
        const Icon = link?.icon ?? fallbackIcon ?? React.Fragment;

        return (
          <CommandItem
            key={link.href}
            keywords={link.keywords}
            onSelect={() => onLinkSelect(link.href, link.openInNewTab)}
          >
            {link?.iconImage ? (
              <Image
                className="rounded-sm corner-squircle supports-corner-shape:rounded-[50%]"
                src={link.iconImage}
                alt={link.title}
                width={16}
                height={16}
                unoptimized
              />
            ) : (
              <Icon />
            )}
            {link.title}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}

type CommandKind = 'command' | 'page' | 'link';

type CommandMetaMap = Map<
  string,
  {
    commandKind: CommandKind;
  }
>;

function buildCommandMetaMap() {
  const commandMetaMap: CommandMetaMap = new Map();

  commandMetaMap.set('Light', { commandKind: 'command' });
  commandMetaMap.set('Dark', { commandKind: 'command' });
  commandMetaMap.set('Auto', { commandKind: 'command' });

  commandMetaMap.set('Copy Mark as SVG', {
    commandKind: 'command',
  });
  commandMetaMap.set('Copy Logotype as SVG', {
    commandKind: 'command',
  });

  SOCIAL_LINK_ITEMS.forEach((item) => {
    commandMetaMap.set(item.title, {
      commandKind: 'link',
    });
  });

  return commandMetaMap;
}

const COMMAND_META_MAP = buildCommandMetaMap();

const ENTER_ACTION_LABELS: Record<CommandKind, string> = {
  command: 'Run Command',
  page: 'Go to Page',
  link: 'Open Link',
};

function CommandMenuFooter() {
  const selectedCommandKind = useCommandState(
    (state) => COMMAND_META_MAP.get(state.value)?.commandKind ?? 'page'
  );

  return (
    <>
      <div className="flex h-10" />

      <div className="absolute inset-x-0 bottom-0 flex h-10 items-center justify-between gap-2 border-t bg-zinc-100/30 px-4 text-xs font-medium dark:bg-zinc-800/30">
        <VictorMark className="size-6 text-muted-foreground" aria-hidden />

        <div className="flex shrink-0 items-center gap-2">
          <span>{ENTER_ACTION_LABELS[selectedCommandKind]}</span>
          <Kbd>
            <CornerDownLeftIcon />
          </Kbd>
          <Separator
            orientation="vertical"
            className="data-[orientation=vertical]:h-4"
          />
          <span className="text-muted-foreground">Exit</span>
          <Kbd>Esc</Kbd>
        </div>
      </div>
    </>
  );
}
